import { useEffect, useState, type FC } from "react";
import { IntervalToggleContainer } from "../CronGenerator.style";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import React from "react";

type CronValues = {
    minute: string;
    hour: string;
    day: string;
    month: string;
    weekDay: string;
};

type HourProps = {
    setCronValues: React.Dispatch<React.SetStateAction<CronValues>>;
    clear: boolean;
};

const HourOptions = {
    EVERY: "Hour",
    RANGE: "Range",
} as const;

const Hours: FC<HourProps> = ({ setCronValues, clear }) => {
    const [activeOption, setActiveOption] = useState<string>(HourOptions.EVERY);
    const [error, setError] = useState(false);
    const [prefixError, setPrefixError] = useState(false);
    const [hour, setHour] = useState("*");
    const [prefixInterval, setPrefixInterval] = useState("*");
    const [hourRange, setHourRange] = useState({ from: "", to: "" });
    const [hourRangeError, setHourRangeError] = useState({ fromError: false, toError: false });
    const [startAtEnabled, setStartAtEnabled] = useState<boolean>(false);

    useEffect(() => {
        setHour("*");
        setPrefixInterval("*");
        setError(false);
        setPrefixError(false);
        setHourRange({ from: "", to: "" });
        setHourRangeError({ fromError: false, toError: false });
        setStartAtEnabled(false);
    }, [activeOption, clear]);


    useEffect(() => {
        if (activeOption === HourOptions.EVERY) {
            const val =
                prefixInterval === "*" || prefixInterval === ""
                    ? hour
                    : hour === "*" || hour === ""
                        ? hour
                        : `${hour}/${prefixInterval}`;
            setCronValues((prev) => ({ ...prev, hour: val }));
        }
    }, [hour, prefixInterval, activeOption, setCronValues]);

    useEffect(() => {
        if (activeOption === HourOptions.RANGE) {
            let rangeVal = "*";
            const from = Number(hourRange.from);
            const to = Number(hourRange.to);
            if (from === 0 && to === 0) {
                rangeVal = "*"
            }
            else if (!isNaN(from) && !isNaN(to) && from <= to && to <= 23) {
                rangeVal = `${from}-${to}`;
            }
            setCronValues((prev) => ({ ...prev, hour: rangeVal }));
        }else {
            setCronValues((prev) => ({ ...prev, hour: "*" }));
        }
    }, [hourRange, activeOption, setCronValues]);

    const handleOptionSelect = (event: SelectChangeEvent) => {
        setActiveOption(event.target.value);
    };

    const handleNumericInput = (
        event: React.ChangeEvent<HTMLInputElement>,
        min: number,
        max: number,
        setValue: (val: string) => void,
        setError: (err: boolean) => void
    ) => {
        const val = event.target.value;
        const num = Number(val);
        if (val === "") {
            setValue("*");
            setError(false);
        } else if (!isNaN(num) && num >= min && num <= max) {
            setValue(val);
            setError(false);
        } else {
            setValue("*");
            setError(true);
        }
    };

    const validateHour = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value)
        if (value >= 0 && value <= 23) {
            setStartAtEnabled(true)
        } else {
            setStartAtEnabled(false)
        }
        handleNumericInput(e, 0, 23, setHour, setError);
    }

    const addPrefix = (e: React.ChangeEvent<HTMLInputElement>) =>
        handleNumericInput(e, 1, 23, setPrefixInterval, setPrefixError);

    const handleHourFromRange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const from = e.target.value;
        const num = Number(from);
        const toNum = Number(hourRange.to);

        setHourRange((prev) => ({ ...prev, from }));
        const fromError = !(from === "" || (num >= 0 && num <= 23));
        if (fromError) {
            setStartAtEnabled(false);
        } else {
            setStartAtEnabled(true);
        }
        const toError = from !== "" && (toNum < num || toNum > 23);

        setHourRangeError({ fromError, toError });
    };

    const handleHourToRange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const to = e.target.value;
        const toNum = Number(to);
        const fromNum = Number(hourRange.from);

        setHourRange((prev) => ({ ...prev, to }));
        const toError =
            hourRange.from === "" || isNaN(toNum) || toNum < fromNum || toNum > 23;

        setHourRangeError((prev) => ({ ...prev, toError }));
    };

    const renderSelector = () => {
        switch (activeOption) {
            case HourOptions.EVERY:
                return (
                    <>
                        <TextField
                            label="Hours"
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            onChange={validateHour}
                            error={error}
                            helperText={error ? "0 to 23" : " "}
                            value={hour === "*" ? "" : hour}
                        />
                        <TextField
                            label="Start at"
                            disabled={!startAtEnabled}
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            onChange={addPrefix}
                            error={prefixError}
                            helperText={prefixError ? "1 to 23" : "*Hours"}
                            value={prefixInterval === "*" ? "" : prefixInterval}
                        />
                    </>
                );
            case HourOptions.RANGE:
                return (
                    <>
                        <TextField
                            label="From"
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            value={hourRange.from}
                            onChange={handleHourFromRange}
                            error={hourRangeError.fromError}
                            helperText={hourRangeError.fromError ? "0 to 23" : " "}
                        />
                        <TextField
                            label="To"
                            variant="standard"
                            disabled={!startAtEnabled}
                            sx={{ m: 1, width: "fit-content" }}
                            value={hourRange.to}
                            onChange={handleHourToRange}
                            error={hourRangeError.toError}
                            helperText={
                                hourRangeError.toError
                                    ? `${Number(hourRange.from) < 23 ? hourRange.from : 0} to 23`
                                    : " "
                            }
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <IntervalToggleContainer style={{ alignItems: "start" }}>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                <InputLabel id="hour-select-label">Hour</InputLabel>
                <Select
                    labelId="hour-select-label"
                    value={activeOption}
                    onChange={handleOptionSelect}
                >
                    <MenuItem value={HourOptions.EVERY}>{HourOptions.EVERY}</MenuItem>
                    <MenuItem value={HourOptions.RANGE}>{HourOptions.RANGE}</MenuItem>
                </Select>
            </FormControl>
            {renderSelector()}
        </IntervalToggleContainer>
    );
};

export default Hours;
